import { useState } from 'react';
import logo from '../assets/logo.png';
import { createDonationOrder, verifyDonationPayment } from '../services/api';
import './Donate.css';

const presets = ['₹101', '₹251', '₹501', '₹1,001', '₹2,501', '₹5,001'];

const causes = [
  { id: 'ganesh', icon: null, title: 'Ganesh Utsav Fund', desc: 'Support decorations, events and cultural programmes during Ganesh Chaturthi.' },
  { id: 'medical', icon: '🏥', title: 'Medical & Health Camps', desc: 'Fund free medical check-ups, blood donation drives and health awareness campaigns.' },
  { id: 'welfare', icon: '🍱', title: 'Community Welfare', desc: 'Help with free food distribution, scholarships and support for underprivileged families.' },
  { id: 'environment', icon: '🌳', title: 'Environmental Activities', desc: 'Support tree plantation, beach clean-up and environment protection initiatives.' },
];

const trustPoints = [
  'Registered charitable trust',
  '80G tax exemption certificate',
  'Monthly expenditure reports published',
  'All donations acknowledged',
  'Governed by a volunteer committee',
];

export default function Donate() {
  const [selectedAmount, setSelectedAmount] = useState('₹501');
  const [customAmount, setCustomAmount] = useState('');
  const [selectedCause, setSelectedCause] = useState('ganesh');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [donorPhone, setDonorPhone] = useState('');
  const [anonymous, setAnonymous] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const amount = customAmount
      ? parseInt(customAmount, 10)
      : parseInt(selectedAmount.replace(/[₹,]/g, ''), 10);

    if (!amount || amount < 10) {
      setError('Please enter a valid amount (minimum ₹10).');
      return;
    }
    if (!window.Razorpay) {
      setError('Payment gateway failed to load. Please refresh the page and try again.');
      return;
    }

    setSubmitting(true);
    try {
      const cause = causes.find((c) => c.id === selectedCause);
      const order = await createDonationOrder({
        amount,
        cause: cause?.title || selectedCause,
        anonymous,
        donorName: anonymous ? undefined : donorName,
        donorEmail: anonymous ? undefined : donorEmail,
        donorPhone: anonymous ? undefined : donorPhone,
      });

      const rzp = new window.Razorpay({
        key: order.keyId,
        amount: order.amount,
        currency: order.currency,
        name: 'Aarambh',
        description: cause?.title || 'Donation',
        image: logo,
        order_id: order.orderId,
        prefill: { name: donorName, email: donorEmail, contact: donorPhone },
        theme: { color: '#C8860A' },
        handler: async (response) => {
          try {
            await verifyDonationPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setSubmitted(true);
          } catch (err) {
            setError(err?.response?.data?.message || 'Payment verification failed. If any amount was deducted, it will be refunded.');
          } finally {
            setSubmitting(false);
          }
        },
        modal: {
          ondismiss: () => setSubmitting(false),
        },
      });

      rzp.on('payment.failed', () => {
        setError('Payment failed. Please try again.');
        setSubmitting(false);
      });

      rzp.open();
    } catch (err) {
      setError(err?.response?.data?.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="donate-page">
        <div className="page-hero">
          <div className="container">
            <div className="badge">Donation</div>
            <h1>Make a Donation</h1>
          </div>
        </div>
        <section>
          <div className="container" style={{ maxWidth: 600, textAlign: 'center' }}>
            <div className="success-card card">
              <div style={{ fontSize: '4rem' }}>🙏</div>
              <h2>Thank You for Your Donation!</h2>
              <p>Your contribution of <strong>{customAmount || selectedAmount}</strong> towards <strong>{causes.find(c => c.id === selectedCause)?.title}</strong> has been received. You will get a confirmation on {donorEmail || 'your email'}.</p>
              <p style={{ color: 'var(--text-light)', fontSize: '0.9rem', marginTop: 12 }}>
                Jai Ganesh <img src={logo} alt="" style={{ height: '18px', verticalAlign: 'middle', display: 'inline' }} /> — May Lord Ganesha bless you with happiness and prosperity.
              </p>
              <button className="btn-primary" style={{ marginTop: 24 }} onClick={() => setSubmitted(false)}>Make Another Donation</button>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="donate-page">
      <div className="page-hero">
        <div className="container">
          <div className="badge">Support the Cause</div>
          <h1>Make a Donation</h1>
          <p>Your generosity fuels our celebrations, empowers social activities and supports families in need across Valsad.</p>
        </div>
      </div>

      <section>
        <div className="container donate-layout">
          {/* Donation Form */}
          <div className="donate-form-col">
            <form className="donate-form card" onSubmit={handleSubmit}>
              <h2>Choose Your Contribution</h2>

              {/* Cause */}
              <div className="form-group">
                <label className="form-label">Select a Cause</label>
                <div className="causes-grid">
                  {causes.map((c) => (
                    <div
                      key={c.id}
                      className={`cause-item${selectedCause === c.id ? ' selected' : ''}`}
                      onClick={() => setSelectedCause(c.id)}
                    >
                      <span className="cause-emoji">
                        {c.id === 'ganesh'
                          ? <img src={logo} alt="Aarambh" style={{ height: '36px', objectFit: 'contain' }} />
                          : c.icon}
                      </span>
                      <span>{c.title}</span>
                    </div>
                  ))}
                </div>
                <p className="cause-desc">{causes.find(c => c.id === selectedCause)?.desc}</p>
              </div>

              {/* Amount */}
              <div className="form-group">
                <label className="form-label">Select Amount</label>
                <div className="amount-grid">
                  {presets.map((amt) => (
                    <button
                      key={amt}
                      type="button"
                      className={`amount-btn${selectedAmount === amt && !customAmount ? ' selected' : ''}`}
                      onClick={() => { setSelectedAmount(amt); setCustomAmount(''); }}
                    >
                      {amt}
                    </button>
                  ))}
                </div>
                <div className="custom-amount">
                  <span className="rupee-prefix">₹</span>
                  <input
                    type="number"
                    placeholder="Enter custom amount"
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(''); }}
                    min="10"
                  />
                </div>
              </div>

              {/* Donor Info */}
              <div className="form-group">
                <label className="form-label">Your Information</label>
                <label className="anonymous-toggle">
                  <input type="checkbox" checked={anonymous} onChange={(e) => setAnonymous(e.target.checked)} />
                  <span>Donate anonymously</span>
                </label>
                {!anonymous && (
                  <div className="donor-fields">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Full Name *"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      required={!anonymous}
                    />
                    <input
                      type="email"
                      className="form-input"
                      placeholder="Email Address *"
                      value={donorEmail}
                      onChange={(e) => setDonorEmail(e.target.value)}
                      required={!anonymous}
                    />
                    <input
                      type="tel"
                      className="form-input"
                      placeholder="Phone Number"
                      value={donorPhone}
                      onChange={(e) => setDonorPhone(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {error && <p style={{ color: '#d32f2f', fontSize: '0.9rem' }}>{error}</p>}
              <button type="submit" className="btn-primary donate-submit" disabled={submitting}>
                {submitting ? 'Processing…' : `🙏 Donate ${customAmount ? `₹${customAmount}` : selectedAmount}`}
              </button>
              <p className="donate-secure">🔒 Secure donation • 80G Tax Receipt available</p>
            </form>
          </div>

          {/* Sidebar */}
          <div className="donate-sidebar">
            <div className="impact-card card">
              <h3>Your Impact</h3>
              <div className="impact-items">
                <div className="impact-item">
                  <span className="impact-amount">₹101</span>
                  <span className="impact-desc">can help feed a family for one day</span>
                </div>
                <div className="impact-item">
                  <span className="impact-amount">₹501</span>
                  <span className="impact-desc">can help sponsor a medical check-up for 5 people</span>
                </div>
                <div className="impact-item">
                  <span className="impact-amount">₹1,001</span>
                  <span className="impact-desc">can help plant 20 saplings & support 2 families</span>
                </div>
                <div className="impact-item">
                  <span className="impact-amount">₹5,001</span>
                  <span className="impact-desc">can help fully sponsor a social activity event</span>
                </div>
              </div>
              <p className="impact-note">*Indicative examples of what your contribution can support — actual allocation depends on funds raised.</p>
            </div>

            <div className="trust-card card">
              <h3>🛡️ Why Trust Us?</h3>
              <ul>
                {trustPoints.map((point) => (
                  <li key={point}>
                    <span className="trust-check">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
